// Design System StatusBadge Component
// Unified status indicator component

import React from 'react';
import { Chip, styled, alpha } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens } from '../../tokens';

// Status configurations
const statusConfig = {
  // General statuses
  active: { color: 'success', label: 'Active' },
  inactive: { color: 'error', label: 'Inactive' },
  pending: { color: 'warning', label: 'Pending' },
  draft: { color: 'default', label: 'Draft' },
  archived: { color: 'default', label: 'Archived' },
  
  // Task/Project statuses
  'not-started': { color: 'default', label: 'Not Started' },
  'in-progress': { color: 'info', label: 'In Progress' },
  completed: { color: 'success', label: 'Completed' },
  cancelled: { color: 'error', label: 'Cancelled' },
  blocked: { color: 'error', label: 'Blocked' },
  
  // Content statuses
  published: { color: 'success', label: 'Published' },
  scheduled: { color: 'info', label: 'Scheduled' },
  review: { color: 'warning', label: 'In Review' },
  rejected: { color: 'error', label: 'Rejected' },
  
  // User/Employee statuses
  online: { color: 'success', label: 'Online' },
  offline: { color: 'default', label: 'Offline' },
  away: { color: 'warning', label: 'Away' },
  busy: { color: 'error', label: 'Busy' },
  
  // Payment/Invoice statuses
  paid: { color: 'success', label: 'Paid' },
  unpaid: { color: 'error', label: 'Unpaid' },
  overdue: { color: 'error', label: 'Overdue' },
  partial: { color: 'warning', label: 'Partial' },
  refunded: { color: 'info', label: 'Refunded' },
  
  // Approval statuses
  approved: { color: 'success', label: 'Approved' },
  'pending-approval': { color: 'warning', label: 'Pending Approval' },
  
  // Custom/fallback
  custom: { color: 'default', label: '' },
};

// Get color palette based on status type
const getStatusColor = (theme, color) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  switch (color) {
    case 'success':
      return {
        bg: alpha(colors.success.main, 0.1),
        color: colors.success.main,
        border: alpha(colors.success.main, 0.3),
      };
    case 'error':
      return {
        bg: alpha(colors.error.main, 0.1),
        color: colors.error.main,
        border: alpha(colors.error.main, 0.3),
      };
    case 'warning':
      return {
        bg: alpha(colors.warning.main, 0.1),
        color: colors.warning.main,
        border: alpha(colors.warning.main, 0.3),
      };
    case 'info':
      return {
        bg: alpha(colors.info.main, 0.1),
        color: colors.info.main,
        border: alpha(colors.info.main, 0.3),
      };
    default:
      return {
        bg: alpha(colors.text.secondary, 0.1),
        color: colors.text.secondary,
        border: alpha(colors.text.secondary, 0.3),
      };
  }
};

// Styled Chip component
const StyledChip = styled(Chip)(({ theme, statusColor, variant }) => {
  const colors = getStatusColor(theme, statusColor);
  
  return {
    borderRadius: borderTokens.sm,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 'auto',
    padding: `${spacingTokens.component.xs} ${spacingTokens.component.sm}`,
    
    ...(variant === 'filled' && {
      backgroundColor: colors.bg,
      color: colors.color,
      border: `1px solid ${colors.border}`,
      
      '& .MuiChip-deleteIcon': {
        color: colors.color,
        opacity: 0.7,
        '&:hover': {
          opacity: 1,
        },
      },
    }),
    
    ...(variant === 'outlined' && {
      backgroundColor: 'transparent',
      color: colors.color,
      borderColor: colors.color,
      
      '&:hover': {
        backgroundColor: colors.bg,
      },
    }),
    
    ...(variant === 'dot' && {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      paddingLeft: spacingTokens.component.xs,
      
      '&::before': {
        content: '""',
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: colors.color,
        marginRight: spacingTokens.component.xs,
      },
    }),
  };
});

/**
 * StatusBadge - Unified status indicator component
 * 
 * Features:
 * - Predefined status types with colors
 * - Custom status support
 * - Multiple variants (filled, outlined, dot)
 * - Size options
 * - Optional delete/action button
 * 
 * @example
 * // Basic usage
 * <StatusBadge status="active" />
 * 
 * // Custom label
 * <StatusBadge status="in-progress" label="Working on it" />
 * 
 * // With dot variant
 * <StatusBadge status="online" variant="dot" />
 * 
 * // Custom status
 * <StatusBadge status="custom" label="Special" color="primary" />
 */
const StatusBadge = ({
  status,
  label,
  variant = 'filled',
  size = 'small',
  color,
  onDelete,
  icon,
  ...props
}) => {
  // Get status configuration
  const config = statusConfig[status] || statusConfig.custom;
  const displayLabel = label || config.label || status;
  const statusColor = color || config.color;
  
  return (
    <StyledChip
      label={displayLabel}
      size={size}
      variant={variant === 'dot' ? 'filled' : variant}
      statusColor={statusColor}
      onDelete={onDelete}
      icon={icon}
      {...props}
    />
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'dot']),
  size: PropTypes.oneOf(['small', 'medium']),
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'success', 'error', 'warning', 'info']),
  onDelete: PropTypes.func,
  icon: PropTypes.element,
};

export default StatusBadge;